import { deliverAnalyticsBeacon } from "./analyticsBeacon";
import { getAccessToken } from "./authToken";
import { readStoredAffiliateCode } from "./affiliateRef";
import { currentDeviceContext } from "./deviceContext";
import { applyCreatorCode, collectTouch, hasCampaignSignal, mergeAuthFirstTouch, mergeFirstTouch, mergeLastTouch } from "./marketingAttribution";
import { signupLayoutName } from "./signupGoogleFirstFlag";
import {
  LANDING_HEADLINE_VARIANT_PROPERTY,
  STORY_SAMPLE_PATH,
  currentLandingSearch,
  landingHeadlineVariant,
} from "./storyHeadlineCopy";
import { storyHeadlineEnabled } from "./storyHeadlineFlag";

const API_ORIGIN = process.env.REACT_APP_API_URL || "https://api.dopletech.com";
const ANONYMOUS_ID_KEY = "dople_marketing_anonymous_id_v1";
const TEST_MODE_KEY = "dople_test";
const SESSION_ID_KEY = "dople_marketing_session_id_v1";
const FIRST_TOUCH_KEY = "dople_marketing_first_touch_v1";
const LAST_TOUCH_KEY = "dople_marketing_last_touch_v1";
const ANONYMOUS_COOKIE = "dople_mid";
const TOUCH_COOKIE = "dople_touch";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;

const storageArea = (name) => {
  try {
    if (typeof window === "undefined") return null;
    const area = window[name];
    if (!area || typeof area.getItem !== "function") return null;
    return area;
  } catch {
    return null;
  }
};

const safeStorage = (storage, operation, fallback = null) => {
  try {
    if (!storage) return fallback;
    return operation(storage);
  } catch {
    return fallback;
  }
};

const createId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const storedId = (storage, key) => {
  const existing = safeStorage(storage, (target) => target.getItem(key), "");
  if (existing) return existing;
  const created = createId();
  safeStorage(storage, (target) => target.setItem(key, created));
  return created;
};

const readJson = (storage, key) => {
  const raw = safeStorage(storage, (target) => target.getItem(key), "");
  try {
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const cookieDomainAttribute = () => {
  const host = window.location.hostname || "";
  if (host === "dopletech.com" || host.endsWith(".dopletech.com")) return "; Domain=.dopletech.com";
  return "";
};

const writeCookie = (name, value) => {
  try {
    if (typeof document === "undefined") return null;
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${cookieDomainAttribute()}${secure}`;
    return true;
  } catch {
    return null;
  }
};

const currentTouch = () => {
  if (typeof window === "undefined") return {};
  return applyCreatorCode(collectTouch({
    search: window.location.search || "",
    pathname: window.location.pathname || "",
    referrer: (() => {
      try {
        return typeof document !== "undefined" ? document.referrer : "";
      } catch {
        return "";
      }
    })(),
    currentHost: window.location.hostname || "",
  }), readStoredAffiliateCode());
};

const rememberTouch = (storageKey, next, previous) => {
  if (JSON.stringify(next) === JSON.stringify(previous)) return;
  safeStorage(storageArea("localStorage"), (storage) => storage.setItem(storageKey, JSON.stringify(next)));
};

function isAuthAttributionSurface(pathname) {
  const path = String(pathname || "").split("?")[0].split("#")[0].toLowerCase();
  const bare = path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
  return bare === "/login"
    || bare.startsWith("/login/")
    || bare === "/signup"
    || bare.startsWith("/signup/")
    || bare === "/oauth/callback"
    || bare.startsWith("/oauth/callback/");
}

const attribution = () => {
  if (typeof window === "undefined") return {};
  const observed = currentTouch();
  const first = readJson(storageArea("localStorage"), FIRST_TOUCH_KEY);
  const last = readJson(storageArea("localStorage"), LAST_TOUCH_KEY);
  const onAuthSurface = isAuthAttributionSurface(window.location.pathname);
  const mergedFirst = onAuthSurface ? mergeAuthFirstTouch(first, observed) : mergeFirstTouch(first, observed);
  const mergedLast = mergeLastTouch(last, observed);
  rememberTouch(FIRST_TOUCH_KEY, mergedFirst, first);
  rememberTouch(LAST_TOUCH_KEY, mergedLast, last);
  const anonymousId = storedId(storageArea("localStorage"), ANONYMOUS_ID_KEY);
  writeCookie(ANONYMOUS_COOKIE, anonymousId);
  const durable = onAuthSurface
    ? (Object.keys(mergedFirst).length ? mergedFirst : observed)
    : (hasCampaignSignal(mergedFirst) ? mergedFirst : observed);
  const encoded = JSON.stringify(durable);
  if (encoded.length <= 1800) writeCookie(TOUCH_COOKIE, encoded);
  if (onAuthSurface) return Object.keys(mergedFirst).length ? mergedFirst : observed;
  return hasCampaignSignal(observed) ? observed : (Object.keys(mergedFirst).length ? mergedFirst : observed);
};

export const marketingSignupFields = () => {
  try {
    if (typeof window === "undefined") return { anonymous_id: "", attribution: {}, device: "", viewport_bucket: "" };
    const device = currentDeviceContext();
    return {
      anonymous_id: storedId(storageArea("localStorage"), ANONYMOUS_ID_KEY),
      attribution: attribution(),
      device: device.device,
      viewport_bucket: device.viewport_bucket,
    };
  } catch {
    return { anonymous_id: "", attribution: {}, device: "", viewport_bucket: "" };
  }
};

export const readFirstTouch = () => {
  if (typeof window === "undefined") return {};
  attribution();
  return readJson(storageArea("localStorage"), FIRST_TOUCH_KEY);
};

const SOURCE_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
  "creator_code",
  "landing_path",
  "story",
  "referrer_host",
  "gclid",
  "gbraid",
  "wbraid",
  "gad_source",
  "gad_campaignid",
  "cta_source",
];

export function isSignupDestination(href) {
  const text = String(href || "").trim();
  if (!text) return false;
  if (text.startsWith("/")) {
    const path = text.split("?")[0].split("#")[0];
    const trimmed = path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
    return trimmed === "/signup";
  }
  try {
    const url = new URL(text);
    const host = String(url.hostname || "").replace(/^www\./, "");
    if (host !== "dopletech.com") return false;
    const path = url.pathname.length > 1 && url.pathname.endsWith("/") ? url.pathname.slice(0, -1) : url.pathname;
    return path === "/signup";
  } catch {
    return false;
  }
}

export function signupCtaProperties(placement, touch) {
  const source = {};
  SOURCE_KEYS.forEach((key) => {
    if (touch && touch[key]) source[key] = String(touch[key]).slice(0, 500);
  });
  return {
    placement: String(placement || "unknown").slice(0, 64),
    source,
  };
}

export function placementForAuthNext(nextPath) {
  const path = String(nextPath || "").split("?")[0].split("#")[0];
  if (path === "/pricing" || path === "/subscribe" || path === "/pay") return "pricing";
  return "login";
}

function barePath(href) {
  const text = String(href || "").trim();
  if (!text.startsWith("/")) return "";
  const path = text.split("?")[0].split("#")[0];
  return path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
}

export const trackSignupCta = (placement, href = "/signup") => {
  try {
    // The Copenhagen sample link is a landing CTA. Other non-signup hrefs stay quiet.
    const storySample = placement === "story_sample_link" && barePath(href) === STORY_SAMPLE_PATH;
    if (!isSignupDestination(href) && !storySample) return;
    const first = readFirstTouch();
    emit("cta_click", signupCtaProperties(placement, first), { beacon: true, attribution: first });
  } catch {
    // A blocked browser must not stop the signup click.
  }
};

const SIGNUP_ERROR_FIELDS = new Set([
  "email",
  "password",
  "re_password",
  "first_name",
  "last_name",
  "non_field",
  "form",
]);

export const trackSignupError = ({ field, error_type } = {}) => {
  const safeField = SIGNUP_ERROR_FIELDS.has(field) ? field : "form";
  const safeType = /^[a-z0-9_]{1,32}$/.test(String(error_type || "")) ? String(error_type) : "invalid";
  emit("signup_error", { field: safeField, error_type: safeType });
};

export const trackOauthFail = (reason) => {
  const safe = String(reason || "").trim().toLowerCase();
  if (!/^[a-z0-9_]{1,64}$/.test(safe)) return;
  emit("oauth_fail", { provider: "google", reason: safe });
};

export const marketingClientContext = () => {
  if (typeof window === "undefined") {
    return { anonymous_id: "", session_id: "", attribution: {} };
  }
  return {
    anonymous_id: storedId(storageArea("localStorage"), ANONYMOUS_ID_KEY),
    session_id: storedId(storageArea("sessionStorage"), SESSION_ID_KEY),
    attribution: attribution(),
  };
};

const withDevice = (properties) => {
  const device = currentDeviceContext();
  const next = { ...(properties && typeof properties === "object" ? properties : {}) };
  if (device.device) next.device = device.device;
  if (device.viewport_bucket) next.viewport_bucket = device.viewport_bucket;
  return next;
};

const eventRequestBody = (eventName, properties, attributionOverride) => JSON.stringify({
  event_name: eventName,
  anonymous_id: storedId(storageArea("localStorage"), ANONYMOUS_ID_KEY),
  session_id: storedId(storageArea("sessionStorage"), SESSION_ID_KEY),
  path: window.location.pathname.slice(0, 500),
  attribution: attributionOverride || attribution(),
  properties: withDevice(properties),
});

const eventRequestHeaders = () => {
  const headers = { "Content-Type": "application/json" };
  const accessToken = getAccessToken();
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  return headers;
};

// Post-deploy checks set ?dople_test=1. Test mode records the event locally
// and skips sendBeacon, fetch, XHR, and image pixels. ?dople_test=0 clears the flag.
export const analyticsTestMode = () => {
  try {
    if (typeof window === "undefined") return false;
    let flag = "";
    try {
      flag = new URLSearchParams(window.location.search || "").get("dople_test") || "";
    } catch {
      flag = "";
    }
    if (flag === "1") {
      safeStorage(storageArea("localStorage"), (storage) => {
        if (typeof storage.setItem === "function") storage.setItem(TEST_MODE_KEY, "1");
      });
      return true;
    }
    if (flag === "0") {
      safeStorage(storageArea("localStorage"), (storage) => {
        if (typeof storage.removeItem === "function") storage.removeItem(TEST_MODE_KEY);
      });
      return false;
    }
    return safeStorage(
      storageArea("localStorage"),
      (storage) => storage.getItem(TEST_MODE_KEY),
      "",
    ) === "1";
  } catch {
    return false;
  }
};

const recordTestEvent = (event) => {
  try {
    if (typeof window === "undefined") return;
    if (!Array.isArray(window.__dopleTestEvents)) window.__dopleTestEvents = [];
    window.__dopleTestEvents.push(event);
  } catch {
    // A blocked page still skips the network send.
  }
  try {
    if (typeof console !== "undefined" && typeof console.debug === "function") console.debug(event);
  } catch {
    // Debug output is optional.
  }
};

const eventObject = (eventName, properties, attributionOverride) => {
  try {
    return JSON.parse(eventRequestBody(eventName, properties, attributionOverride));
  } catch {
    return {
      event_name: eventName,
      properties: properties && typeof properties === "object" ? properties : {},
    };
  }
};

const serverEmit = (eventName, properties, attributionOverride) => {
  if (typeof window === "undefined" || !eventName) return;
  fetch(`${API_ORIGIN}/api/analytics/events`, {
    method: "POST",
    credentials: "include",
    keepalive: true,
    headers: eventRequestHeaders(),
    body: eventRequestBody(eventName, properties, attributionOverride),
  }).catch(() => {});
};

const serverEmitUnload = (eventName, properties, attributionOverride) => {
  if (typeof window === "undefined" || !eventName) return;
  deliverAnalyticsBeacon({
    url: `${API_ORIGIN}/api/analytics/events`,
    body: eventRequestBody(eventName, properties, attributionOverride),
    headers: eventRequestHeaders(),
  });
};

export const emit = (name, payload = {}, options = {}) => {
  if (typeof window === "undefined" || !name) return;
  const attributionOverride = options.attribution;
  let testMode = false;
  try {
    testMode = analyticsTestMode();
  } catch {
    testMode = false;
  }
  if (testMode) recordTestEvent(eventObject(name, payload, attributionOverride));
  else if (options.beacon) serverEmitUnload(name, payload, attributionOverride);
  else serverEmit(name, payload, attributionOverride);
  if (typeof window.__dopleTrackEvent === "function") {
    try { window.__dopleTrackEvent(name, payload); } catch {}
  }
  if (!testMode && typeof window.gtag === "function") {
    try { window.gtag("event", name, payload); } catch {}
  }
};

const PAGE_VIEW_HANDOFF_KEY = "dople_page_view_handoff_v1";

const normalizedViewPath = (value) => {
  const path = String(value || "/").split("?")[0].split("#")[0];
  if (path.length > 1 && path.endsWith("/")) return path.slice(0, -1);
  return path || "/";
};

const signupPathname = (path) => {
  const bare = String(path || "").split("?")[0].split("#")[0];
  return bare.length > 1 && bare.endsWith("/") ? bare.slice(0, -1) : bare;
};

const signupLayoutValue = (value) => (
  value === "google_first" || value === "classic" ? value : signupLayoutName()
);

export const trackPageView = (path, properties) => {
  try {
    analyticsTestMode();
  } catch {
    // Test mode must not block the view.
  }
  const safePath = String(path || "").slice(0, 500);
  const extra = properties && typeof properties === "object" ? { ...properties } : {};
  if (signupPathname(safePath) === "/signup") {
    extra.signup_layout = signupLayoutValue(extra.signup_layout);
  }
  try {
    const staticPath = window.__DOPLE_STATIC_PAGE_VIEW;
    if (staticPath && normalizedViewPath(staticPath) === normalizedViewPath(safePath)) return;
    window.__DOPLE_PAGE_VIEW_TRACKED = safePath;
    safeStorage(storageArea("sessionStorage"), (storage) => {
      storage.setItem(PAGE_VIEW_HANDOFF_KEY, JSON.stringify({ path: safePath, at: Date.now() }));
    });
  } catch {
    // A blocked browser still records the view.
  }
  if (signupPathname(safePath) === "/" && storyHeadlineEnabled()) {
    extra[LANDING_HEADLINE_VARIANT_PROPERTY] = landingHeadlineVariant(currentLandingSearch());
  }
  emit("page_view", { path: safePath, ...extra });
};

export const trackSignupStarted = (method = "email", properties) => {
  const extra = properties && typeof properties === "object" ? properties : {};
  emit("signup_started", { method, signup_layout: signupLayoutValue(extra.signup_layout) });
};

export const trackLoginStarted = (method = "email") => emit("login_started", { method });
export const trackCheckoutStarted = (product, meta = {}) => emit("checkout_started", { product, ...meta });
export const trackSurveyOpened = (meta = {}) => emit("survey_opened", meta);
export const trackSurveyStarted = (meta = {}) => emit("survey_started", meta);
export const trackSurveyProgress = (meta = {}) => emit("survey_progress", meta);
export const trackSurveyCompleted = (meta = {}) => emit("survey_completed", meta);
export const trackSurveyQuestionShown = (meta = {}) => emit("survey_question_shown", meta);
export const trackSurveyInputModeSwitched = (meta = {}) => emit("survey_input_mode_switched", meta);
export const trackSurveyVoicePermission = (meta = {}) => emit("survey_voice_permission", meta);
export const trackSurveyExit = (meta = {}) => emit("survey_exit", meta, { beacon: true });

export const trackContentSearch = (query, meta = {}) => emit("guide_search_performed", {
  query_length: String(query || "").length,
  ...meta,
});

export const trackContentFilter = ({ filter, query }) => emit("guide_filter_clicked", {
  filter,
  has_query: Boolean(query),
});

export const trackContentCardClick = ({ slug, type, route, filter, query }) => emit("guide_card_clicked", {
  slug,
  type,
  route,
  filter,
  has_query: Boolean(query),
});

export const trackContentCtaClick = ({
  cta, ctaLabel, location, content_slug, content_section, cta_position, cta_href, cta_label, ...rest
} = {}) => emit("content_cta_clicked", {
  content_slug: content_slug || null,
  content_section: content_section || null,
  cta_label: cta_label || ctaLabel || cta || null,
  cta_position: cta_position || location || null,
  cta_href: cta_href || null,
  ...rest,
});

export const trackNoOp = () => {};
